const OFFICIAL_ADMIN_PHONE = '18100136698';

export const SITE_SETTINGS_COLLECTION = 'site_settings';
export const MAINTENANCE_DOC_ID = 'maintenance';
/** 维护期间管理员本次浏览器会话已验证（sessionStorage，刷新/关页后需重登） */
export const MAINTENANCE_SESSION_KEY = 'yugui_maintenance_admin_verified';

export const DEFAULT_MAINTENANCE_CONFIG = {
  enabled: false,
  title: '网站维护中',
  message: '',
  startAt: null,
  endAt: null,
  updatedAt: null,
  updatedByPhone: '',
  updatedByName: ''
};

export const normalizeMaintenanceConfig = (doc) => {
  if (!doc) return { ...DEFAULT_MAINTENANCE_CONFIG };
  return {
    ...DEFAULT_MAINTENANCE_CONFIG,
    enabled: !!doc.enabled,
    title: doc.title || DEFAULT_MAINTENANCE_CONFIG.title,
    message: doc.message || '',
    startAt: doc.startAt ?? null,
    endAt: doc.endAt ?? null,
    updatedAt: doc.updatedAt ?? null,
    updatedByPhone: doc.updatedByPhone || '',
    updatedByName: doc.updatedByName || ''
  };
};

/** 管理员（含超级管理员、临时管理员）可在维护期间正常使用 */
export const isMaintenanceBypassProfile = (profile) => {
  if (!profile) return false;
  if (String(profile.phone || '') === OFFICIAL_ADMIN_PHONE) return true;
  const role = profile.role;
  return role === 'super_admin' || role === 'admin';
};

export const isMaintenanceActive = (config) => {
  if (!config?.enabled) return false;
  const now = Date.now();
  if (config.startAt && now < Number(config.startAt)) return false;
  if (config.endAt && now > Number(config.endAt)) return false;
  return true;
};

export const markMaintenanceAdminSession = (phone) => {
  if (!phone) return;
  try {
    sessionStorage.setItem(
      MAINTENANCE_SESSION_KEY,
      JSON.stringify({ phone: String(phone), at: Date.now() })
    );
  } catch (_) {
    /* ignore */
  }
};

export const clearMaintenanceAdminSession = () => {
  try {
    sessionStorage.removeItem(MAINTENANCE_SESSION_KEY);
  } catch (_) {
    /* ignore */
  }
};

export const hasMaintenanceAdminSession = (phone) => {
  if (!phone) return false;
  try {
    const raw = sessionStorage.getItem(MAINTENANCE_SESSION_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return String(parsed?.phone || '') === String(phone);
  } catch (_) {
    return false;
  }
};

/** 维护期间：未登录、普通用户、或未在本会话重验的管理员，均须看到维护页 */
export const shouldBlockForMaintenance = (config, profile) => {
  if (!isMaintenanceActive(config)) return false;
  if (!profile) return true;
  if (!isMaintenanceBypassProfile(profile)) return true;
  return !hasMaintenanceAdminSession(profile.phone);
};

export const formatMaintenanceDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('zh-CN', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatMaintenanceWindow = (config) => {
  const start = config?.startAt ? formatMaintenanceDateTime(config.startAt) : null;
  const end = config?.endAt ? formatMaintenanceDateTime(config.endAt) : null;
  if (start && end) return `${start} 至 ${end}`;
  if (start) return `自 ${start} 起`;
  if (end) return `预计 ${end} 前恢复`;
  return '请留意平台后续通知';
};

export const buildMaintenanceNotice = (config) => {
  const windowText = formatMaintenanceWindow(config);
  const custom = (config?.message || '').trim();
  const defaultBody = `很抱歉，予归平台正在进行系统维护与功能升级，期间暂时无法为普通用户提供服务。\n\n维护时间：${windowText}\n\n感谢您的理解与耐心等待，维护完成后将第一时间恢复访问。`;
  return {
    title: config?.title || '网站维护中',
    body: custom || defaultBody,
    windowText
  };
};

export const getMaintenanceBlockToast = (config) => {
  const { title, windowText } = buildMaintenanceNotice(config);
  return `${title}。维护时间：${windowText}。仅管理员可在维护页重新登录进入。`;
};

export const fetchCloudMaintenanceConfig = async (db, ensureAuth) => {
  if (!db) return { ...DEFAULT_MAINTENANCE_CONFIG };
  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    const res = await db.collection(SITE_SETTINGS_COLLECTION).doc(MAINTENANCE_DOC_ID).get();
    let doc = null;
    if (Array.isArray(res?.data)) doc = res.data[0];
    else if (res?.data && typeof res.data === 'object') doc = res.data;
    if (doc) return normalizeMaintenanceConfig(doc);
    const fallback = await db
      .collection(SITE_SETTINGS_COLLECTION)
      .where({ key: MAINTENANCE_DOC_ID })
      .limit(1)
      .get();
    return normalizeMaintenanceConfig(fallback?.data?.[0]);
  } catch (error) {
    console.warn('维护模式配置读取失败:', error);
    return { ...DEFAULT_MAINTENANCE_CONFIG };
  }
};

export const pickMaintenanceConfig = (cloudCfg, serverCfg) => {
  const cloud = normalizeMaintenanceConfig(cloudCfg);
  const server = normalizeMaintenanceConfig(serverCfg);
  const cloudTs = Number(cloud.updatedAt || 0);
  const serverTs = Number(server.updatedAt || 0);
  if (serverTs && serverTs >= cloudTs) return server;
  if (cloudTs && cloudTs > serverTs) return cloud;
  if (server.enabled) return server;
  return cloud;
};

export const fetchServerMaintenanceConfig = async (apiRequest) => {
  if (typeof apiRequest !== 'function') return null;
  try {
    const res = await apiRequest('/site/maintenance', 'GET');
    if (res?.success && res.maintenance) return normalizeMaintenanceConfig(res.maintenance);
  } catch (error) {
    console.warn('服务端维护配置读取失败:', error);
  }
  return null;
};

export const saveServerMaintenanceConfig = async (apiRequest, payload) => {
  if (typeof apiRequest !== 'function') return null;
  try {
    const res = await apiRequest('/admin/maintenance', 'POST', payload);
    if (res?.success && res.maintenance) return normalizeMaintenanceConfig(res.maintenance);
    return res;
  } catch (error) {
    console.warn('服务端维护配置写入失败:', error);
    return null;
  }
};

export const saveCloudMaintenanceConfig = async (db, ensureAuth, payload) => {
  if (!db) return false;
  try {
    if (typeof ensureAuth === 'function') await ensureAuth();
    const docPayload = {
      ...payload,
      key: MAINTENANCE_DOC_ID,
      updatedAt: Date.now()
    };
    try {
      await db.collection(SITE_SETTINGS_COLLECTION).doc(MAINTENANCE_DOC_ID).update(docPayload);
      return true;
    } catch (_) {
      try {
        await db.collection(SITE_SETTINGS_COLLECTION).doc(MAINTENANCE_DOC_ID).set(docPayload);
        return true;
      } catch (e2) {
        const existing = await db
          .collection(SITE_SETTINGS_COLLECTION)
          .where({ key: MAINTENANCE_DOC_ID })
          .limit(1)
          .get();
        if (existing?.data?.length) {
          await db.collection(SITE_SETTINGS_COLLECTION).doc(existing.data[0]._id).update(docPayload);
        } else {
          await db.collection(SITE_SETTINGS_COLLECTION).add({
            ...docPayload,
            createdAt: Date.now()
          });
        }
        return true;
      }
    }
  } catch (error) {
    console.warn('维护模式配置写入失败:', error);
    return false;
  }
};

export const toDatetimeLocalValue = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const fromDatetimeLocalValue = (value) => {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
};
